import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, AlertTriangle, Timer, DollarSign, LineChart, ArrowRight, Upload } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import OnboardingSplash from "@/components/OnboardingSplash";
import { api } from "@/lib/api";

const CARDS = [
  { key: "reorder_count", label: "Reorder", to: "/app/reorder", icon: ShoppingCart, color: "#14624A", note: "SKUs below target coverage" },
  { key: "stockout_risk_count", label: "Stockout Risk", to: "/app/reorder", icon: AlertTriangle, color: "#C9943D", note: "Under 14 days of supply" },
  { key: "slow_count", label: "Slow Inventory", to: "/app/slow", icon: Timer, color: "#3B424B", note: "Capital tied up" },
  { key: "pricing_count", label: "Pricing", to: "/app/pricing", icon: DollarSign, color: "#14624A", note: "Margin below threshold" },
  { key: "market_count", label: "Market Comparison", to: "/app/market", icon: LineChart, color: "#3B424B", note: "Requires authorized source" },
];

export default function Dashboard() {
  const [demo, setDemo] = useState(false);
  const [data, setData] = useState(null);
  const [marketCount, setMarketCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/analytics/overview", { params: { demo } }),
      api.get("/analytics/market", { params: { demo } }),
    ])
      .then(([o, m]) => { setData(o.data); setMarketCount((m.data.items || []).length); })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [demo]);

  return (
    <DashboardLayout demoBanner={demo} demoMode={demo} onToggleDemo={() => setDemo(!demo)}>
      <OnboardingSplash />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid="dashboard-heading">This Week's Opportunities</h1>
          <p className="mt-1 text-sm text-[#3B424B]">Actionable signals from your inventory & sales data.</p>
        </div>
        {demo && <span className="tm-demo-tag" data-testid="demo-badge">Illustrative Demo Data</span>}
      </div>

      {loading ? (
        <div className="mt-8 text-sm text-[#3B424B]">Loading analytics…</div>
      ) : error ? (
        <div className="tm-card p-6 mt-8 bg-white" data-testid="dashboard-error"><div className="font-semibold text-[#D9381E]">Couldn't load analytics</div><p className="mt-1 text-sm text-[#3B424B]">{error}</p></div>
      ) : !data?.has_data ? (
        <div className="tm-card p-8 mt-8 flex flex-col sm:flex-row items-start gap-5" data-testid="dashboard-empty">
          <div className="h-12 w-12 rounded-lg bg-[var(--tm-charcoal)] text-white flex items-center justify-center"><Upload size={20} /></div>
          <div className="flex-1">
            <div className="font-semibold text-lg">No data uploaded yet</div>
            <p className="mt-2 text-sm text-[#3B424B]">Upload an inventory export and recent sales/POS data to see actionable recommendations. Prefer to look around first?</p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Link to="/app/upload" className="tm-btn-primary" data-testid="dashboard-cta-upload">Upload Data</Link>
              <button onClick={() => setDemo(true)} className="tm-btn-outline" data-testid="dashboard-cta-demo">Explore with demo data</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {CARDS.map((c) => {
              const v = c.key === "market_count" ? marketCount : (data.summary?.[c.key] ?? 0);
              return (
                <Link to={c.to} key={c.key} className="tm-card p-5 bg-white hover:border-[var(--tm-border-strong)] transition-colors" data-testid={`opp-card-${c.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: c.color, color: c.color === "#C9943D" ? "#0F1115" : "white" }}><c.icon size={16} /></div>
                    <ArrowRight size={16} className="text-[#3B424B]" />
                  </div>
                  <div className="mt-4 text-3xl font-bold font-mono">{v}</div>
                  <div className="text-sm font-semibold mt-1">{c.label}</div>
                  <div className="text-xs text-[#3B424B] mt-1">{c.note}</div>
                </Link>
              );
            })}
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-4">
            <div className="tm-card p-6 bg-white">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Observation Window</div>
              <div className="mt-2 text-2xl font-bold font-mono">{data.observed_days} days</div>
              <p className="mt-2 text-sm text-[#3B424B]">Analysis uses your most recent sales activity to calculate velocity and days of supply.</p>
            </div>
            <div className="tm-card p-6 bg-white">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Total SKUs Analyzed</div>
              <div className="mt-2 text-2xl font-bold font-mono">{data.summary.total_skus}</div>
              <p className="mt-2 text-sm text-[#3B424B]">Every recommendation includes evidence and a confidence level so you always know why.</p>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
